import type { SORTING_ORDER } from '@/contants/common';
import type { UPLOAD_DATA_ROW } from '@/views/screens/PurchaseOrderScreens/PurchaseOrder/components/upload-packing-list/UploadPackingList';
import type {
  CustomerPurchaseOrder,
  CustomerPurchaseOrderItem,
  PurchaseOrderItem,
  PurchaseOrderListItem,
} from '@/views/screens/PurchaseOrderScreens/PurchaseOrder/types';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { convertDateESTToUTCToUserLocal, convertDateUTCToUserLocal, truncateTimeZoneDate } from '../../utilz/dates';
import { DEFAULT_STORE_OBJECT } from '../constants';
import type { DefaultStoreObject } from '../types';
import PurchaseOrderTypes from './PurchaseOrderTypes';

export interface PurchaseOrderState extends Record<string, any> {
  purchaseOrderList: DefaultStoreObject<CustomerPurchaseOrder[]>;
  purchaseOrderFilters: {
    vendorKey?: number;
    partNumber: string;
    poNumber: string;
    branchKey?: string;
    orderShipDateFrom?: string | moment.Moment;
    orderShipDateTo?: string | moment.Moment;
    orderCreatedDateFrom?: string | moment.Moment;
    orderCreatedDateTo?: string | moment.Moment;
    receiptNumber?: string;
    povStatusCode: string; // O
    povStatus?: string; // O
    pageIndex: number;
    pageSize: number;
    orderByColumn: string;
    orderBySort?: SORTING_ORDER;
    customerKey?: number;
    pageLoading?: boolean; // ???
    incoCode?: string;
    partDesription?: string;
    userGuid?: string; // redundant?
  };
  totalPORecords: number;
  isCardView: boolean;
  selectedPurchaseOrders: CustomerPurchaseOrder[] | PurchaseOrderListItem[];
  purchaseOrderSidebarVisible: boolean;
  purchaseOrderSidebarExportVisible: boolean;
  poTMSSettings: Record<string, unknown>; // TODO: investigate and remove, seems redundant
  checkedAll: boolean;
  packingListData: UPLOAD_DATA_ROW[];
  purchaseOrderDetails: DefaultStoreObject<CustomerPurchaseOrderItem[]>;
  selectedRegularOrders?: {
    data: CustomerPurchaseOrderItem[] | PurchaseOrderItem[];
    releaseOrders?: CustomerPurchaseOrderItem[] | PurchaseOrderItem[];
    selectedAll?: boolean;
  };
  releaseOrderInfo: CustomerPurchaseOrderItem[] | PurchaseOrderItem[];
}

const initialState: PurchaseOrderState = {
  purchaseOrderList: { ...DEFAULT_STORE_OBJECT, data: [] },
  purchaseOrderFilters: {
    vendorKey: undefined,
    partNumber: '',
    poNumber: '',
    branchKey: undefined,
    orderShipDateFrom: moment(new Date()).subtract(6, 'months'),
    orderShipDateTo: moment(new Date()).add(6, 'month'),
    orderCreatedDateFrom: '',
    orderCreatedDateTo: '',
    receiptNumber: '',
    povStatusCode: 'O',
    povStatus: 'O',
    pageIndex: 1,
    pageSize: 10,
    orderByColumn: '',
  },
  selectedPurchaseOrders: [],
  purchaseOrderSidebarVisible: false,
  purchaseOrderDetails: { ...DEFAULT_STORE_OBJECT, data: [] },
  purchaseOrderSidebarExportVisible: false,
  draftOrderSearchExportVisible: false,
  selectedRegularOrders: undefined,
  regularOrderInfo: { ...DEFAULT_STORE_OBJECT, data: [] },
  releaseOrderInfo: [],
  purchaseOrderShipmentData: [],
  checkedAll: false,
  isCardView: true,
  totalPORecords: 0,
  poStatusData: [],
  packingListData: [],
  poTMSSettings: {},
};

// biome-ignore lint/style/useDefaultParameterLast: <explanation>
const PurchaseOrderReducer = (state = initialState, { type, payload }: any): PurchaseOrderState => {
  switch (type) {
    case PurchaseOrderTypes.PO_STATUS_DATA: {
      return {
        ...state,
        poStatusData: payload,
      };
    }

    case PurchaseOrderTypes.PO_IS_CARD_VIEW: {
      return {
        ...state,
        isCardView: payload,
      };
    }

    case PurchaseOrderTypes.GET_PURCHASE_ORDER_LIST_PROGRESS: {
      return {
        ...state,
        purchaseOrderList: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: state.purchaseOrderList.data,
        },
      };
    }

    case PurchaseOrderTypes.GET_PURCHASE_ORDER_LIST_ERROR: {
      return {
        ...state,
        purchaseOrderList: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [...(state.purchaseOrderList.data || [])],
        },
      };
    }

    case PurchaseOrderTypes.SET_SELECT_ALL_CHECK: {
      if (payload?.data) {
        state.purchaseOrderList.data?.forEach((element) => {
          if (element.uniqueId === payload.data.uniqueId) {
            element.isChecked = payload?.checked || false;
          }
        });
      } else {
        state.purchaseOrderList.data?.forEach((element) => {
          element.isChecked = payload?.checked || false;
        });
      }
      const findCheckItem = state.purchaseOrderList.data?.filter((item) => item.isChecked === true);

      let temp = false;
      if (!!state.purchaseOrderList.data?.length && findCheckItem?.length === state.purchaseOrderList.data.length) {
        temp = true;
      }

      return {
        ...state,
        checkedAll: temp,
        purchaseOrderList: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: state.purchaseOrderList.data,
        },
      };
    }

    case PurchaseOrderTypes.GET_PURCHASE_ORDER_LIST_SUCCESS: {
      // @ts-ignore
      payload.data.forEach((element) => {
        element.uniqueId = uuidv4();
        element.isChecked = state.checkedAll || false;
        element.createdOn = truncateTimeZoneDate(convertDateESTToUTCToUserLocal(element.createdOn));
        element.orderShipDate = truncateTimeZoneDate(convertDateESTToUTCToUserLocal(element.orderShipDate));
        element.orderDeliveryDate = truncateTimeZoneDate(convertDateESTToUTCToUserLocal(element.orderDeliveryDate));
      });

      let listdata = [...(state.purchaseOrderList.data || []), ...payload.data];
      state.selectedPurchaseOrders.forEach((ele) => {
        ele.isChecked = true;
      });
      listdata = [...state.selectedPurchaseOrders, ...listdata];
      const uniqueList = listdata.filter((obj, index, self) => {
        return index === self.findIndex((o) => o.rowID === obj.rowID);
      });

      return {
        ...state,
        purchaseOrderList: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: uniqueList,
        },
      };
    }

    case PurchaseOrderTypes.SET_PURCHASE_ORDER_LIST: {
      return {
        ...state,
        purchaseOrderList: {
          ...DEFAULT_STORE_OBJECT,
          totalRecords: 0,
          ...payload,
        },
      };
    }

    case PurchaseOrderTypes.SET_PURCHASE_ORDER_FILTERS: {
      return {
        ...state,
        purchaseOrderFilters: {
          ...state.purchaseOrderFilters,
          ...payload,
        },
      };
    }

    case PurchaseOrderTypes.SET_PURCHASE_ORDER_SELECTED_DATA: {
      return {
        ...state,
        selectedPurchaseOrders: payload,
      };
    }

    case PurchaseOrderTypes.SET_PURCHASE_ORDER_SIDEBAR_TOGGLE: {
      return {
        ...state,
        purchaseOrderSidebarVisible: payload,
      };
    }

    case PurchaseOrderTypes.GET_PURCHASE_ORDER_INFO_PROGRESS: {
      return {
        ...state,
        purchaseOrderDetails: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: state.purchaseOrderDetails.data,
        },
      };
    }

    case PurchaseOrderTypes.GET_PURCHASE_ORDER_INFO_ERROR: {
      return {
        ...state,
        purchaseOrderDetails: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [...(state.purchaseOrderDetails.data || []), ...payload.data],
        },
      };
    }

    case PurchaseOrderTypes.GET_PURCHASE_ORDER_INFO_SUCCESS: {
      // @ts-ignore
      const formattedData = payload.data.map((element) => ({
        ...element,
        expShipDate: element.expShipDate ? truncateTimeZoneDate(convertDateUTCToUserLocal(element.expShipDate)) : null,
        orderShipDate: truncateTimeZoneDate(convertDateESTToUTCToUserLocal(element.orderShipDate)),
      }));
      return {
        ...state,
        purchaseOrderDetails: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: formattedData,
        },
      };
    }

    case PurchaseOrderTypes.SET_PURCHASE_ORDER_INFO: {
      return {
        ...state,
        purchaseOrderDetails: {
          ...DEFAULT_STORE_OBJECT,
          ...payload,
        },
      };
    }

    case PurchaseOrderTypes.SET_PURCHASE_ORDER_EXPORT_TOGGLE: {
      return {
        ...state,
        purchaseOrderSidebarExportVisible: payload,
      };
    }

    case PurchaseOrderTypes.SET_DRAFT_ORDER_SEARCH_EXPORT_TOGGLE: {
      return {
        ...state,
        draftOrderSearchExportVisible: payload,
      };
    }

    case PurchaseOrderTypes.SET_REGULAR_ORDER_SELECTED_DATA: {
      return {
        ...state,
        selectedRegularOrders: payload,
      };
    }

    case PurchaseOrderTypes.SET_RELEASE_ORDER_DATA: {
      return {
        ...state,
        releaseOrderInfo: payload,
      };
    }

    case PurchaseOrderTypes.SET_PURCHASE_ORDER_SHIPMENT: {
      return {
        ...state,
        purchaseOrderShipmentData: payload,
      };
    }

    case PurchaseOrderTypes.SET_TOTAL_PO_RECORDS: {
      return {
        ...state,
        totalPORecords: payload,
      };
    }

    case PurchaseOrderTypes.SET_PACKING_LIST_DATA: {
      return {
        ...state,
        packingListData: payload,
      };
    }

    case PurchaseOrderTypes.PO_TMS_SETTINGS: {
      return {
        ...state,
        poTMSSettings: payload,
      };
    }

    default:
      return state;
  }
};

export default PurchaseOrderReducer;
